<template>
  <v-container class="aoi-editor-container">
    <v-row row wrap align-content-start class="content-container">
      <v-col class="text-center col-8">
        <v-btn-toggle
          v-model="enabledSelection"
          :mandatory="isSelectionMandatory"
          class="mb-3 white--text"
          active-class="darken-2"
        >
          <v-btn
            :disabled="!enabledSelection"
            height="35"
            color="blue-grey"
            class="white--text hyp-selection-btn"
            value="country"
          >
            Select countries
          </v-btn>
          <v-btn
            :disabled="!enabledSelection"
            height="35"
            color="blue-grey"
            class="white--text hyp-selection-btn"
            value="city"
          >
            Select cities
          </v-btn>
          <v-btn
            :disabled="!enabledSelection"
            height="35"
            color="blue-grey"
            class="white--text hyp-selection-btn"
            value="worldwide"
          >
            Worldwide
          </v-btn>
          <v-btn
            v-if="hasWebGL"
            :disabled="!enabledSelection"
            height="35"
            color="blue-grey"
            class="white--text hyp-selection-btn"
            value="customaoi"
          >
            Custom location
          </v-btn>
        </v-btn-toggle>

        <Map
          v-if="hasWebGL"
          :id="mapId"
          height="55vh"
          :show-base-layer-control="false"
          :show-search-control="true"
          :show-places="true"
          :options="mapOptions"
          class="hyp-alert-edit-map"
          @map-on-load="handleMapOnLoad"
        ></Map>
      </v-col>
      <v-col class="col-4">
        <v-container class="py-0 mt-2">
          <v-row row wrap>
            <v-col class="col-12">
              <div class="aoi-results-container">
                <Loader v-if="!enabledSelection" text="" size="small"></Loader>

                <div class="scrollable" v-if="buttonCountryEnabled">
                  <div class="mb-2 hyp-text-helper">
                    <div v-if="hasWebGL">
                      Click on countries within the map to select or unselect
                      them.
                    </div>
                    <div
                      class="mt-2"
                      :class="{
                        'red--text':
                          selectedCountries &&
                          selectedCountries.length === countriesMaxCount,
                      }"
                    >
                      You may select up to {{ countriesMaxCount }} countries.
                    </div>
                  </div>
                  <v-chip
                    v-for="(country, index) in selectedCountries"
                    :key="index"
                    :close="true"
                    @click:close="handleRemoveCountry(country)"
                    small
                    class="aoi-chip pull-left mr-1 mb-1"
                  >
                    <span :title="country.displayName" class="aoi-chip-span">
                      {{ country.displayName }}
                    </span>
                  </v-chip>
                  <div class="clearfix"></div>
                </div>

                <div class="scrollable" v-if="buttonCityEnabled">
                  <div class="mb-2 hyp-text-helper">
                    <div v-if="hasWebGL">
                      Click on city boxes within the map to select or unselect
                      them.
                    </div>
                    <div
                      class="mt-2"
                      :class="{
                        'red--text':
                          selectedCities &&
                          selectedCities.length === citiesMaxCount,
                      }"
                    >
                      You may select up to {{ citiesMaxCount }} cities.
                    </div>
                  </div>
                  <v-chip
                    v-for="(city, index) in selectedCities"
                    :key="index"
                    :close="true"
                    @click:close="handleRemoveCity(city)"
                    class="aoi-chip pull-left mr-1"
                    small
                  >
                    <span
                      :title="city.displayName + ', ' + city.countryName"
                      class="aoi-chip-span"
                    >
                      {{ city.displayName }}, {{ city.countryName }}
                    </span>
                  </v-chip>
                  <div class="clearfix"></div>
                </div>

                <div v-if="buttonCustomAoi">
                  <div v-if="!aoiFeature.geometry" class="hyp-text-helper">
                    Click on the map to begin creating your custom location. Add
                    as many vertices as you like and then click on the first
                    vertex to finish the location.
                  </div>
                  <div class="hyp-location-text" v-if="totalArea">
                    Your location's area is
                    {{ totalArea.toLocaleString() }} km<sup>2</sup>
                  </div>
                  <div
                    v-if="isAreaTooLarge"
                    class="red--text mt-2 hyp-text-helper"
                  >
                    Your location's area is greater than the max allowed,
                    {{ customAoiMaxArea.toLocaleString() }} km<sup>2</sup>.
                  </div>
                  <div v-if="isAreaTooLarge" class="mt-2">
                    Delete the location using the trashcan icon in the top right
                    of the map and start over OR click on vertices and move them
                    to resize the location.
                  </div>
                  <div
                    class="mt-2 hyp-text-helper"
                    v-if="aoiFeature.geometry && !isAreaTooLarge"
                  >
                    <p>
                      To move your custom location, click on it within the map
                      to grab it and move it.
                    </p>
                    <p>
                      To edit your custom location, click on it within the map,
                      click it a second time so you see small points along the
                      lines between each vertex. You may now select any vertex
                      and move it or select a point along a line to create a new
                      vertex.
                    </p>
                    <p>
                      Note: When your location on the map is blue it is not
                      editable. You must click on it to turn it green for it to
                      be editable.
                    </p>
                  </div>
                </div>

                <div v-if="buttonWorldwide">
                  <v-chip small class="aoi-chip"> Worldwide </v-chip>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";
import { MODULE_PREFIX_MAP, MODULE_PREFIX_SEARCH } from "@/store";
import { addDrawControls, removeDrawControls } from "@/common/maps/maps";
import { removeMapData } from "@/common/maps/features";
import {
  ALERT_AOI_EDITOR_MAP,
  ALERT_AOI_MAX_AREA,
  WORLD_WIDE_AOI,
  CUSTOM_AOI,
} from "@/common/constants/alert.js";
import { getBboxFromFeatures } from "@/common/maps/utilities";
import {
  addToMap as addCountriesToMap,
  addToMap as addCitiesToMap,
} from "@/services/grids/stability.service";
import {
  getAllCountries,
  getAllCities,
  getFeatureArea,
} from "@/common/maps/utilities";
import { getMapDrawStyles } from "@/common/maps/styles";
import { EVENT_CATEGORIES, EVENT_ACTIONS } from "@/common/constants/analytics";
import { pageTrackerLogger } from "@/common/pageTracker";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

export default {
  name: "AoiEditor",
  components: {
    Map: () => import("@/components/map/Map"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    let aoiFeature = {
      type: "Feature",
      properties: { name: "CUSTOM AOI" },
    };

    if (
      lodashGet(this.value, "geometry") ||
      lodashGet(this.value, "properties.aoiIds")
    ) {
      aoiFeature = this.value;
    }

    return {
      hasWebGL: this.isAvailableWebGL(),
      aoiFeature: aoiFeature,
      drawControls: null,
      customAoiMaxArea: ALERT_AOI_MAX_AREA,
      mapId: ALERT_AOI_EDITOR_MAP,
      mapLoaded: false,
      mapOptions: {
        zoom: 0,
        bounds: [-175, -85, 178, 85],
        attributionCompact: true,
      },
      countries: null,
      countriesAll: [],
      countriesLayerId: "layer-country",
      countriesLayerFieldId: "_id",
      countriesMaxCount: 5,
      cities: null,
      citiesAll: [],
      citiesLayerId: "layer-city",
      citiesLayerFieldId: "_id",
      citiesMaxCount: 5,
      enabledSelection: null,
      isSelectionMandatory: false,
    };
  },
  async mounted() {
    this.countriesMaxCount = this.getAlertCountryLimit();
    this.citiesMaxCount = 25;

    if (!this.hasWebGL) {
      this.countriesAll = await getAllCountries();
      this.citiesAll = await getAllCities();
      if (this.isWorldWideAOI) {
        this.enabledSelection = "worldwide";
      } else {
        if (this.hasCities) {
          this.cities = this.aoiFeature.properties.aoiIds;
          this.enabledSelection = "city";
        } else {
          this.countries = this.aoiFeature.properties.aoiIds;
          this.enabledSelection = "country";
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      location: MODULE_PREFIX_SEARCH + "location",
    }),
    map() {
      return this.maps[this.mapId];
    },
    isWorldWideAOI() {
      const aoiFeatureName = lodashGet(this.aoiFeature, "properties.name");
      return aoiFeatureName === WORLD_WIDE_AOI.properties.name;
    },
    hasGeometry() {
      return !!lodashGet(this.aoiFeature, "geometry");
    },
    hasCountriesOrCities() {
      const AOI_IDS = lodashGet(this.aoiFeature, "properties.aoiIds");
      return !!(AOI_IDS && Array.isArray(AOI_IDS) && AOI_IDS.length);
    },
    hasCountries() {
      const AOI_IDS = lodashGet(this.aoiFeature, "properties.aoiIds");
      if (
        AOI_IDS &&
        Array.isArray(AOI_IDS) &&
        AOI_IDS.length &&
        this.countriesAll &&
        Array.isArray(this.countriesAll) &&
        this.countriesAll.length
      ) {
        const LEN = this.countriesAll.length;
        for (let i = 0; i < LEN; i++) {
          const COUNTRY = this.countriesAll[i];
          if (
            lodashGet(COUNTRY, "properties.iso2") &&
            COUNTRY.properties.iso2.toUpperCase() === AOI_IDS[0].toUpperCase()
          ) {
            return true;
          }
        }
      }
      return false;
    },
    hasCities() {
      const AOI_IDS = lodashGet(this.aoiFeature, "properties.aoiIds");
      if (
        AOI_IDS &&
        Array.isArray(AOI_IDS) &&
        AOI_IDS.length &&
        this.citiesAll &&
        Array.isArray(this.citiesAll) &&
        this.citiesAll.length
      ) {
        const LEN = this.citiesAll.length;
        for (let i = 0; i < LEN; i++) {
          const CITY = this.citiesAll[i];
          if (
            lodashGet(CITY, "properties.code") &&
            CITY.properties.code.toUpperCase() === AOI_IDS[0].toUpperCase()
          ) {
            return true;
          }
        }
      }
      return false;
    },
    totalArea() {
      let area;
      if (this.aoiFeature.geometry) {
        area = getFeatureArea(this.aoiFeature);
        area = Math.round(area / 1000000);
      }
      return area;
    },
    isAreaTooLarge() {
      if (this.totalArea) {
        return this.totalArea > this.customAoiMaxArea;
      }
      return false;
    },
    buttonCountryEnabled() {
      return this.enabledSelection === "country";
    },
    buttonCityEnabled() {
      return this.enabledSelection === "city";
    },
    buttonCustomAoi() {
      return this.enabledSelection === "customaoi";
    },
    buttonWorldwide() {
      return this.enabledSelection === "worldwide";
    },
    selectedCountries() {
      let selected;
      if (
        this.countries &&
        Array.isArray(this.countries) &&
        this.countries.length
      ) {
        selected = [];
        this.countries.forEach((code) => {
          if (code) {
            const COUNTRY = lodashFind(this.countriesAll, (country) => {
              const CODE = lodashGet(country, "properties.iso2");
              if (CODE) {
                return CODE.toUpperCase() === code.toUpperCase();
              }
            });
            if (COUNTRY) {
              selected.push(COUNTRY.properties);
            }
          }
        });
      }
      return selected;
    },
    selectedCities() {
      let selected;
      if (this.cities && Array.isArray(this.cities) && this.cities.length) {
        selected = [];
        this.cities.forEach((code) => {
          if (code) {
            const CITY = lodashFind(this.citiesAll, (city) => {
              const CODE = lodashGet(city, "properties.code");
              if (CODE) {
                return CODE.toUpperCase() === code.toUpperCase();
              }
            });
            if (CITY) {
              selected.push(CITY.properties);
            }
          }
        });
      }
      return selected;
    },
  },
  methods: {
    async handleMapOnLoad() {
      this.mapLoaded = true;
      this.map.resize();
      if (this.hasGeometry) {
        if (this.isWorldWideAOI) {
          this.enabledSelection = "worldwide";
        } else {
          this.enabledSelection = "customaoi";
          this.map.on("moveend", this.drawAoiOnMap);
        }
      } else {
        this.countriesAll = await getAllCountries();
        this.citiesAll = await getAllCities();
        if (this.hasCities) {
          this.cities = this.aoiFeature.properties.aoiIds;
          this.enabledSelection = "city";
        } else {
          this.countries = this.aoiFeature.properties.aoiIds;
          this.enabledSelection = "country";
        }
      }
      this.fitToFeature();

      // the v-btn-toggle needs to have the mandatory prop set to true
      // but it was causing the map to act weird if it was set before
      // the map was ready so this variable sets the prop after map ready
      this.isSelectionMandatory = true;
    },
    fitToFeature() {
      if (this.hasGeometry || this.hasCountriesOrCities) {
        lodashDebounce(() => {
          let features = [this.aoiFeature];
          if (this.hasCountriesOrCities) {
            let layerId = this.countriesLayerId;
            let fieldId = this.countriesLayerFieldId;
            if (this.hasCities) {
              layerId = this.citiesLayerId;
              fieldId = this.citiesLayerFieldId;
            }
            features = this.map.queryRenderedFeatures(null, {
              layers: [layerId],
              filter: [
                "match",
                ["get", fieldId],
                this.aoiFeature.properties.aoiIds,
                true,
                false,
              ],
            });
          }
          if (
            features &&
            Array.isArray(features) &&
            features.length &&
            lodashGet(features[0], "geometry")
          ) {
            const BBOX = getBboxFromFeatures(features);
            this.map.fitBounds(BBOX, { padding: 20 });
          }
        }, 1000)();
      }
    },
    mapCreateEvent(e) {
      this.addFeature(e.features);
    },
    async mapDeleteEvent() {
      this.aoiFeature.geometry = null;
      await this.$emit(EMITTED_ACTIONS.CHANGE, this.aoiFeature);
      this.drawControls.changeMode("draw_polygon");
    },
    mapUpdateEvent(e) {
      this.addFeature(e.features);
    },
    mapActionableEvent(data) {
      const trashButton = document.querySelector(
        ".hyp-alert-edit-map .mapboxgl-ctrl-group .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_trash"
      );
      if (trashButton) {
        this.showButtonTrash(false);
        if (lodashGet(data, "actions.trash") && this.hasGeometry) {
          this.showButtonTrash(true);
        }
      }
    },
    updateAoiStyle() {
      const STYLES = getMapDrawStyles();
      STYLES.forEach((style) => {
        if (
          style.id.indexOf("gl-draw-polygon-and-line-vertex-stroke-inactive") <
          0
        ) {
          let props = [];
          let color;
          if (style.type === "circle") {
            props.push("circle-color");
            color = style.paint["circle-color"];
          } else if (style.type === "fill") {
            props.push("fill-color");
            props.push("fill-outline-color");
            color = style.paint["fill-color"];
          } else {
            props.push("line-color");
            color = style.paint["line-color"];
          }
          color = this.isAreaTooLarge ? "#ff0000" : color;
          props.forEach((prop) => {
            this.map.setPaintProperty(`${style.id}.cold`, prop, color);
            this.map.setPaintProperty(`${style.id}.hot`, prop, color);
          });
        }
      });
    },
    async addFeature(features) {
      if (features && features.length > 0) {
        this.aoiFeature = features[0];
        this.aoiFeature.properties.name = "CUSTOM AOI";
        this.updateAoiStyle();
        await this.$emit(EMITTED_ACTIONS.CHANGE, this.aoiFeature);
      }
    },
    setUpDrawTools() {
      const DRAW_MODE = this.hasGeometry ? "simple_select" : "draw_polygon";

      this.drawControls = addDrawControls(this.map, {
        displayControlsDefault: false,
        defaultMode: DRAW_MODE,
        controls: {
          polygon: true,
          trash: true,
        },
        createEvent: this.mapCreateEvent,
        deleteEvent: this.mapDeleteEvent,
        updateEvent: this.mapUpdateEvent,
        actionableEvent: this.mapActionableEvent,
      });
      this.showButtonDraw(false);
      this.showButtonTrash(false);
    },

    // NOTE (Nolan): I don't know why but adding just the slightest pause helps with rendering
    //    on the map. So, If anyone can think of a better way of doing this please let me know.
    drawAoiOnMap: lodashDebounce(function () {
      this.map.off("moveend", this.drawAoiOnMap);
      this.drawControls.add(this.aoiFeature);
    }, 50),
    showButtonDraw(show) {
      const drawButton = document.querySelector(
        ".hyp-alert-edit-map .mapboxgl-ctrl-group .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_polygon"
      );
      if (drawButton) {
        drawButton.style.display = show ? "block" : "none";
      }
    },
    showButtonTrash(show) {
      const trashButton = document.querySelector(
        ".hyp-alert-edit-map .mapboxgl-ctrl-group .mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_ctrl-draw-btn.mapbox-gl-draw_trash"
      );
      if (trashButton) {
        trashButton.style.display = show ? "block" : "none";
      }
    },
    enableCountrySelection() {
      this.aoiFeature = CUSTOM_AOI;
      this.cities = null;
      if (this.hasWebGL) {
        this.removeCitiesLayer();
        this.addCountriesLayer();
        if (this.drawControls) {
          removeDrawControls(this.map, this.drawControls);
          this.drawControls = null;
        }
      }
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.alert_wizard,
        EVENT_ACTIONS.clicked,
        EVENT_CATEGORIES.alert_aoi_select_country
      );
    },
    enableCitySelection() {
      this.aoiFeature = CUSTOM_AOI;
      this.countries = null;
      if (this.hasWebGL) {
        this.removeCountriesLayer();
        this.addCitiesLayer();
        lodashDebounce(() => {
          this.highlightCities();
        }, 2000)();
        if (this.drawControls) {
          removeDrawControls(this.map, this.drawControls);
          this.drawControls = null;
        }
      }
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.alert_wizard,
        EVENT_ACTIONS.clicked,
        "Select Cities"
      );
    },
    enableCustomAoi() {
      if (
        !this.aoiFeature ||
        lodashGet(this.aoiFeature, "properties.name") ===
          lodashGet(WORLD_WIDE_AOI, "properties.name")
      ) {
        this.aoiFeature = CUSTOM_AOI;
      }
      this.countries = null;
      this.cities = null;
      this.removeCountriesLayer();
      this.removeCitiesLayer();
      this.setUpDrawTools();
      if (!this.aoiFeature.geometry) {
        this.drawControls.changeMode("draw_polygon");
      }
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.alert_wizard,
        EVENT_ACTIONS.clicked,
        EVENT_CATEGORIES.alert_aoi_custom_drawn
      );
    },
    enableWorldwide() {
      this.aoiFeature = WORLD_WIDE_AOI;
      this.countries = null;
      this.cities = null;
      if (this.hasWebGL) {
        this.removeCountriesLayer();
        this.removeCitiesLayer();
        if (this.drawControls) {
          removeDrawControls(this.map, this.drawControls);
          this.drawControls = null;
        }
      }
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.alert_wizard,
        EVENT_ACTIONS.clicked,
        EVENT_CATEGORIES.alert_world_wide
      );
    },
    handleRemoveCountry(country) {
      if (
        country &&
        country.iso2 &&
        this.countries &&
        Array.isArray(this.countries) &&
        this.countries.length
      ) {
        const countryId = country.iso2.toUpperCase();
        this.countries.splice(this.countries.indexOf(countryId), 1);

        if (
          lodashGet(this.aoiFeature, "properties.aoiProperties." + countryId)
        ) {
          delete this.aoiFeature.properties.aoiProperties[countryId];
        }
      }
    },
    handleRemoveCity(city) {
      if (
        city &&
        city.code &&
        this.cities &&
        Array.isArray(this.cities) &&
        this.cities.length
      ) {
        const cityIndex = this.cities.indexOf(city.code.toLowerCase());
        this.cities.splice(cityIndex, 1);
      }
    },
    addCountriesLayer() {
      this.removeCountriesLayer();
      let params = {
        id: "country",
        type: "country",
        sourceId: "source-country",
        layerId: this.countriesLayerId,
        map: this.map,
        aoiCode: "none",
        aoiCodeField: this.countriesLayerFieldId,
        aoiCodeFillColor: "rgba(75, 73, 255, 0.35)",
        mapClickEvent: this.handleMapClickCountries,
      };
      if (this.hasCountries) {
        params.aoiCode = this.aoiFeature.properties.aoiIds;
      }
      addCountriesToMap(params);
    },
    addCitiesLayer() {
      this.removeCitiesLayer();
      let params = {
        id: "city",
        type: "city",
        sourceId: "source-city",
        layerId: this.citiesLayerId,
        map: this.map,
        aoiCode: "none",
        aoiCodeField: this.citiesLayerFieldId,
        aoiCodeFillColor: "rgba(75, 73, 255, 0.35)",
        mapClickEvent: this.handleMapClickCities,
      };
      if (this.hasCities) {
        params.aoiCode = this.aoiFeature.properties.aoiIds;
      }
      addCitiesToMap(params);
    },
    removeCountriesLayer() {
      removeMapData(this.map, this.countriesLayerId);
    },
    removeCitiesLayer() {
      removeMapData(this.map, this.citiesLayerId);
    },
    handleMapClickCountries: lodashDebounce(function (e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        const FEATURE = features[0];
        const ID = lodashGet(
          FEATURE,
          `properties.${this.countriesLayerFieldId}`
        );

        if (ID) {
          let selected =
            this.countries && Array.isArray(this.countries)
              ? lodashCloneDeep(this.countries)
              : [];
          this.aoiFeature.properties.aoiProperties =
            this.aoiFeature.properties.aoiProperties || {};
          if (selected.indexOf(ID) > -1) {
            // REMOVING
            selected.splice(selected.indexOf(ID), 1);
            delete this.aoiFeature.properties.aoiProperties[ID];
          } else {
            // ADDING
            if (selected.length <= this.countriesMaxCount - 1) {
              selected.push(ID);
              this.aoiFeature.properties.aoiProperties[ID] = FEATURE.properties;
            }
          }
          this.countries = selected;
        }
      }
    }, 250),
    handleMapClickCities: lodashDebounce(function (e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        const FEATURE = features[0];
        let ID = lodashGet(FEATURE, `properties.${this.citiesLayerFieldId}`);
        if (ID) {
          ID = ID.toLowerCase();
          let selected =
            this.cities && Array.isArray(this.cities)
              ? lodashCloneDeep(this.cities)
              : [];

          if (selected.indexOf(ID) > -1) {
            selected.splice(selected.indexOf(ID), 1);
          } else {
            if (selected.length <= this.citiesMaxCount - 1) {
              selected.push(ID);
            }
          }
          this.cities = selected;
        }
      }
    }, 250),
    highlightCountries() {
      if (
        this.hasWebGL &&
        this.mapLoaded &&
        this.map.getLayer(this.countriesLayerId)
      ) {
        let items =
          this.countries &&
          Array.isArray(this.countries) &&
          this.countries.length
            ? this.countries
            : "none";

        this.map.setPaintProperty(this.countriesLayerId, "fill-opacity", [
          "match",
          ["get", this.countriesLayerFieldId],
          items,
          0.99,
          0.01,
        ]);

        this.map.setPaintProperty(this.countriesLayerId, "fill-color", [
          "match",
          ["get", this.countriesLayerFieldId],
          items,
          "rgba(75, 73, 255, 0.35)",
          "rgba(255, 255, 255, 0.01)",
        ]);

        this.map.setPaintProperty(this.countriesLayerId, "fill-outline-color", [
          "match",
          ["get", this.countriesLayerFieldId],
          items,
          "rgba(75, 73, 255, 0.99)",
          "rgba(255, 255, 255, 0.2)",
        ]);
      }
    },
    highlightCities() {
      if (
        this.hasWebGL &&
        this.mapLoaded &&
        this.map.getLayer(this.citiesLayerId)
      ) {
        let items =
          this.cities && Array.isArray(this.cities) && this.cities.length
            ? this.cities
            : "none";

        this.map.setPaintProperty(this.citiesLayerId, "fill-opacity", 0.99);

        this.map.setPaintProperty(this.citiesLayerId, "fill-color", [
          "match",
          ["get", this.citiesLayerFieldId],
          items,
          "rgba(75, 73, 255, 0.35)",
          "rgba(255, 255, 255, 0.01)",
        ]);

        this.map.setPaintProperty(this.citiesLayerId, "fill-outline-color", [
          "match",
          ["get", this.citiesLayerFieldId],
          items,
          "rgba(75, 73, 255, 0.99)",
          "rgba(255, 255, 255, 0.75)",
        ]);
      }
    },
    getAoiProperties() {
      if (lodashGet(this.aoiFeature, "properties.aoiIds")) {
        let layerId = this.countriesLayerId;
        let fieldId = this.countriesLayerFieldId;
        if (this.buttonCityEnabled) {
          layerId = this.citiesLayerId;
          fieldId = this.citiesLayerFieldId;
        }
        const FEATURES = this.map.queryRenderedFeatures(null, {
          layers: [layerId],
          filter: [
            "match",
            ["get", fieldId],
            this.aoiFeature.properties.aoiIds,
            true,
            false,
          ],
        });
        let properties = {};
        FEATURES.forEach((feature) => {
          const ID = lodashGet(feature, `properties.${fieldId}`);
          properties[ID] = feature.properties;
        });
        return properties;
      }
      return null;
    },
    handlePageTrackerEvent(category, action, name) {
      let _paq = (window._paq = window._paq || []);
      _paq.push(["trackEvent", category, action, name]);
      pageTrackerLogger(
        "Page Tracker Event " +
          category +
          " " +
          action +
          " " +
          name +
          " logged."
      );
    },
  },
  watch: {
    enabledSelection: {
      async handler() {
        if (this.buttonCountryEnabled) {
          this.enableCountrySelection();
        } else if (this.buttonCityEnabled) {
          this.enableCitySelection();
        } else if (this.buttonCustomAoi) {
          this.enableCustomAoi();
        } else {
          this.enableWorldwide();
        }
        await this.$emit(EMITTED_ACTIONS.CHANGE, this.aoiFeature);
      },
    },
    countries: {
      handler() {
        this.highlightCountries();
        this.aoiFeature.properties.aoiIds = this.countries;
        this.$emit(EMITTED_ACTIONS.CHANGE, this.aoiFeature);
      },
      deep: true,
    },
    cities: {
      handler() {
        this.highlightCities();
        this.aoiFeature.properties.aoiIds = this.cities;
        this.aoiFeature.properties.aoiProperties = this.getAoiProperties();
        this.$emit(EMITTED_ACTIONS.CHANGE, this.aoiFeature);
      },
      deep: true,
    },
    location: {
      handler() {
        if (this.buttonCustomAoi && !this.aoiFeature.geometry) {
          this.drawControls.changeMode("draw_polygon");
        }
      },
      deep: true,
    },
  },
  beforeDestroy() {
    if (this.hasWebGL) {
      this.removeCountriesLayer();
      this.removeCitiesLayer();
    }
    this.drawControls = null;
    this.countries = null;
    this.countriesAll = null;
    this.cities = null;
    this.citiesAll = null;
  },
};
</script>

<style scoped>
.aoi-editor-container {
  padding: 0;
}
.aoi-results-container {
  margin-top: 43px;
  min-height: 200px;
  padding: 10px;
  background-color: white;
  border: 1px solid #e4e4e4;
}
.aoi-chip-span {
  max-width: 210px;
  overflow-x: hidden;
  text-overflow: ellipsis;
}
.aoi-chip >>> .v-chip__content {
  height: 24px;
  border-radius: 16px;
  padding: 0 6px;
}
.underline {
  text-decoration: underline;
}
.hyp-selection-btn >>> .v-btn__content {
  padding: 0 10px;
}
.hyp-location-text {
  font-family: TruenoRg;
  font-weight: bold;
}
.hyp-text-helper {
  font-size: 12px;
}
.scrollable {
  overflow-y: auto;
  max-height: 50vh;
}
</style>
